// Hook to get current user ID and info for workspace operations

import { useAuth } from '@/hooks/useAuth';
import { isConfigEnabled } from '@/config/config';

/**
 * Hook to get the current user ID for workspace operations
 * Handles both authenticated and mock scenarios
 */
export function useCurrentUserId(): string {
  const { user } = useAuth();
  
  // In mock API mode, use consistent mock user ID
  if (isConfigEnabled('REACT_APP_USE_MOCK_API')) {
    return 'mock_user_123';
  }
  
  // Check if we should use mock auth or real auth
  if (isConfigEnabled('REACT_APP_USE_MOCK_AUTH')) {
    return 'mock_user_123';
  }
  
  // In production with real auth, use authenticated user ID
  if (!user?.id) {
    throw new Error('User must be authenticated to access workspace operations');
  }
  
  return user.id;
}

/**
 * Hook to get current user information for display purposes
 * Returns both ID and display name
 */
export function useCurrentUserInfo(): { id: string; name: string; email: string } {
  const { user } = useAuth();
  
  // Only use mock users when BOTH mock auth AND mock API are enabled
  // This allows real auth + mock API for development
  if (isConfigEnabled('REACT_APP_USE_MOCK_AUTH') && isConfigEnabled('REACT_APP_USE_MOCK_API')) {
    return {
      id: 'mock_user_123',
      name: 'Development User',
      email: 'dev.user@company.com'
    };
  }
  
  // In all other cases, use real authenticated user info
  if (!user?.id) {
    throw new Error('User must be authenticated to access workspace operations');
  }
  
  return {
    id: user.id,
    name: user.fullName || user.email?.split('@')[0] || 'User',
    email: user.email || 'user@onyxinsight.com'
  };
}

/**
 * Hook to get user ID with optional override
 * Useful for admin operations
 */
export function useUserIdOrOverride(overrideUserId?: string): string {
  const currentUserId = useCurrentUserId();
  return overrideUserId || currentUserId;
}

// Ensure this file is treated as a module
export {};
